import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Guid } from 'guid-typescript';
import { BehaviorSubject, Observable, OperatorFunction, throwError } from 'rxjs';
import { catchError, distinctUntilChanged, finalize, retry } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

export abstract class WebApiService {
  protected readonly catalogUrl = `${environment.catalogApiUrl}`;
  protected readonly offersUrl = `${environment.offerApiUrl}/offers`;
  private readonly isBusySubject$: BehaviorSubject<boolean>;
  public readonly isBusy$: Observable<boolean>;

  private requestCounter = 0;

  protected constructor(protected readonly http: HttpClient, protected readonly snackBar: MatSnackBar) {
    this.isBusySubject$ = new BehaviorSubject<boolean>(false);
    this.isBusy$ = this.isBusySubject$
      .asObservable()
      .pipe(distinctUntilChanged());
  }

  protected post<T>(pojectId: Guid, resourceName: string, body: T): Observable<T> {
    return this.http.post<T>(`${this.offersUrl}/${pojectId}/${resourceName}`, body, { responseType: 'json' })
      .pipe(this.handleApiRequest());
  }

  protected get<T>(pojectId: Guid, resourceName: string): Observable<T> {
    return this.http.get<T>(`${this.offersUrl}/${pojectId}/${resourceName}`, { responseType: 'json' })
      .pipe(this.handleApiRequest());
  }

  protected put<T>(pojectId: Guid, resourceName: string, body: T): Observable<T> {
    return this.http.put<T>(
      `${this.offersUrl}/${pojectId}/${resourceName}`, body, { responseType: 'json' })
        .pipe(this.handleApiRequest());
  }

  protected delete<T>(pojectId: Guid, resourceName: string): Observable<T> {
    return this.http.delete<T>(`${this.offersUrl}/${pojectId}/${resourceName}`, { responseType: 'json' })
      .pipe(this.handleApiRequest());
  }

  protected handleApiRequest<T>(): OperatorFunction<T, T> {
    return (source: Observable<T>): Observable<T>  => {
      this.requestStarted();
      return source.pipe(
        handleHttpRequest(),
        finalize(() => this.requestFinalized()),
        catchError(error => {
          this.snackBar.open('Es ist ein Fehler aufgetreten. Bitte versuchen sie es später noch ein mal.', undefined,
          {
            politeness: 'assertive',
            duration: 5000,
            verticalPosition: 'top',
          });
          throw error;
        })
      );
    };
  }

  private requestStarted(): void {
    this.requestCounter++;
    this.updateIsBusy();
  }

  private requestFinalized(): void {
    this.requestCounter--;
    this.updateIsBusy();
  }

  private updateIsBusy(): void {
    this.isBusySubject$.next(this.requestCounter > 0);
  }
}

export function handleHttpRequest<T>(): OperatorFunction<T, T> {
  return (source: Observable<T>): Observable<T>  => {
    return source.pipe(
      retry(3), // retry a failed request up to 3 times
      catchError(handleHttpErrorResponse) // then handle the error
      );
  };
}

function handleHttpErrorResponse(errorResponse: HttpErrorResponse): Observable<never> {
  if (errorResponse.error instanceof ErrorEvent) {
      // A client-side or network error occurred. Handle it accordingly.
      console.error('An error occurred:', errorResponse.error.message);
  }
  else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong,
      console.error(
      `${errorResponse.url} returned code ${errorResponse.status}, ` +
      `body was: ${errorResponse.error}`);
  }
  // return an observable with a user-facing error message
  return throwError(
      'Error occured; Please try again later.');
}
